"use client";

import { useClientTranslations } from "@/lib/translate";
import clsx from "clsx";
import { Search01Icon } from "hugeicons-react";
import { useEffect, useRef, useState } from "react";
import { useSearchJob } from "@/modules/hooks/job/useSearchJob";
import { Spinner } from "../commons/icon/Spinner";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import { useSearchBusiness } from "@/modules/hooks/business/useSearchBusiness";
import { useSearchSkills } from "@/modules/hooks/metadata/useSearchSkills";
import { Skill } from "@/modules/types/metadata/metadata";
import { useRouter } from "next/navigation";

export const Banner = ({ skills }: { skills?: Skill[] }) => {
  const { t } = useClientTranslations("home-banner");

  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { jobSuggestions, handleSearchJobSuggestions, loadingJobSuggestions } =
    useSearchJob();
  const {
    businessSuggestions,
    handleSearchBusiness,
    loadingBusinessSuggestions,
  } = useSearchBusiness();
  const { skillSuggestions, handleSearchSkills, loadingSkillSuggestions } =
    useSearchSkills();
  const router = useRouter();

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    handleSearchJobSuggestions(e);
    handleSearchBusiness(e);
    handleSearchSkills(e);
  };

  useEffect(() => {
    if (searchTerm.length > 0) {
      const event = {
        target: { value: searchTerm },
        preventDefault: () => {},
        persist: () => {},
      } as React.ChangeEvent<HTMLInputElement>;
      event.target.value = searchTerm;
      handleSearchJobSuggestions(event);
      handleSearchBusiness(event);
      handleSearchSkills(event);
    }
  }, [searchTerm]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    router.push(`${routes.job.index}?q=${encodeURIComponent(searchTerm)}`);
    // Handle form submission logic here
  };

  return (
    <section className="relative min-h-[500px] w-full bg-gradient-to-r from-primary via-primaryLight to-primaryLighter pt-[70px]">
      {/* Overlay with slight dark gradient */}
      <div className="absolute w-full h-full top-0 left-0 inset-0 dark:bg-black/50"></div>

      {/* Content Container */}
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-primaryForeground sm:text-5xl md:text-6xl">
            {t("title")}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-base text-primaryForeground/90 sm:text-lg md:mt-5 md:max-w-3xl md:text-xl">
            {t("subtitle")}
          </p>

          {/* Search Box */}
          <div className="mx-auto mt-10 max-w-xl">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <div ref={searchContainerRef} className="relative flex-1">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={onSearchTermChange}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Job title, keywords, or company"
                  className="w-full rounded-lg border-0 px-4 py-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-primary"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <Search01Icon className="h-5 w-5 text-gray-400" />
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && searchTerm && (
                  <div className="absolute mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-10 max-h-[300px] overflow-y-auto">
                    <div className="py-2 divide-y divide-gray-100">
                      <div className="px-2">
                        <h3 className="text-start text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
                          {"jobs"}
                        </h3>
                        <div className="space-y-1">
                          {loadingJobSuggestions ? (
                            <div className="w-full px-4 py-3 text-sm text-gray-500 h-10 flex justify-center items-center">
                              <Spinner />
                            </div>
                          ) : jobSuggestions && jobSuggestions.length > 0 ? (
                            jobSuggestions.map((job, index) => (
                              <Link
                                key={index}
                                href={`${routes.job.detailPath}${job.slug}`}
                                className={clsx(
                                  "block w-full text-left px-3 py-2 text-sm text-gray-500",
                                  "transition-colors duration-300",
                                  "hover:bg-primaryLighter hover:text-primaryForeground rounded-md"
                                )}
                              >
                                {job.jobTitle}
                              </Link>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-gray-300">
                              <p className="w-full text-start">
                                No suggestions found
                              </p>
                            </div>
                          )}
                        </div>
                        <h3 className="text-start text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
                          {"companies"}
                        </h3>
                        <div className="space-y-1">
                          {loadingBusinessSuggestions ? (
                            <div className="w-full px-4 py-3 text-sm text-gray-500 h-10 flex justify-center items-center">
                              <Spinner />
                            </div>
                          ) : businessSuggestions &&
                            businessSuggestions.length > 0 ? (
                            businessSuggestions.map((business, index) => (
                              <Link
                                key={index}
                                href={`${routes.company.detailPath}${business.slug}`}
                                className={clsx(
                                  "block w-full text-left px-3 py-2 text-sm text-gray-500",
                                  "transition-colors duration-300",
                                  "hover:bg-primaryLighter hover:text-primaryForeground rounded-md"
                                )}
                              >
                                {business.shortCompanyName}
                              </Link>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-gray-300">
                              <p className="w-full text-start">
                                No suggestions found
                              </p>
                            </div>
                          )}
                        </div>
                        <h3 className="text-start text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
                          {"skills"}
                        </h3>
                        <div className="space-y-1 flex flex-wrap px-1 gap-2 w-full">
                          {loadingSkillSuggestions ? (
                            <div className="w-full px-4 py-3 text-sm text-gray-500 h-10 flex justify-center items-center">
                              <Spinner />
                            </div>
                          ) : skillSuggestions &&
                            skillSuggestions.length > 0 ? (
                            skillSuggestions.map((skill, index) => (
                              <Link
                                key={index}
                                href={`${routes.job.index}?q=${encodeURIComponent(skill.name)}`}
                                className={clsx(
                                  "rounded-full text-secondaryForeground bg-black/5 px-3 py-1 hover:bg-black/10 text-sm mt-1 whitespace-nowrap"
                                )}
                              >
                                {skill.name}
                              </Link>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-gray-300">
                              <p className="w-full text-start">
                                No suggestions found
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="submit"
                className={clsx(
                  "rounded-lg bg-secondary px-6 py-3 text-secondaryForeground transition-colors duration-150",
                  "hover:bg-secondaryLight focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2"
                )}
              >
                Search Jobs
              </button>
            </form>

            {/* List skills */}
            {skills && skills.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-primaryForeground">
                <span>Skills:</span>
                {skills.map((skill) => (
                  <button
                    key={skill.id}
                    className="rounded-full bg-white/20 px-3 py-1 hover:bg-white/30"
                    onClick={() => {
                      setSearchTerm(skill.name);
                      setShowSuggestions(false);
                    }}
                  >
                    {skill.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          {/* <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              { number: "1M+", label: "Active Jobs" },
              { number: "500K+", label: "Companies" },
              { number: "10M+", label: "Job Seekers" },
            ].map((stat) => (
              <div key={stat.label} className="text-primaryForeground">
                <p className="text-4xl font-bold">{stat.number}</p>
                <p className="text-xl">{stat.label}</p>
              </div>
            ))}
          </div> */}
        </div>
      </div>
    </section>
  );
};

Banner.displayName = "Banner";
